// src/components/AnimatedButton.js
import React from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { LIQUID_SPRING, useRipple } from "../utils/liquidMotion";

/**
 * AnimatedButton
 * Props:
 *  - variant: "primary" | "ghost" (matches CSS .btn-primary / .btn-ghost)
 *  - size: "sm" | "md" | "lg"
 *  - className: additional classes
 *  - ...props: native button props
 *
 * Usage:
 *  <AnimatedButton onClick={...}>Save</AnimatedButton>
 *  <AnimatedButton variant="ghost" size="sm">Cancel</AnimatedButton>
 */
export default function AnimatedButton({
  variant = "primary",
  size = "md",
  className = "",
  children,
  onClick,
  style,
  ...props
}) {
  const reduce = useReducedMotion();
  const { ripples, addRipple } = useRipple();

  const baseClass = `btn ${variant === "primary" ? "btn-primary" : "btn-ghost"} ${
    size === "sm" ? "btn-sm" : size === "lg" ? "btn-lg" : ""
  } ${className}`;

  // Liquid micro interactions: a real squash on press (not just a nudge) and
  // a soft ambient glow on hover. Disabled entirely for reduced-motion users.
  const whileTap = reduce ? {} : { scale: 0.95, scaleY: 0.9 };
  const whileHover = reduce ? {} : { scale: 1.015, y: -2, boxShadow: "0 10px 30px -6px rgba(99,102,241,0.4)" };

  const handleClick = (e) => {
    if (!reduce) addRipple(e);
    onClick?.(e);
  };

  return (
    <motion.button
      {...props}
      onClick={handleClick}
      whileTap={whileTap}
      whileHover={whileHover}
      transition={LIQUID_SPRING}
      className={baseClass + " animated-press"}
      style={{ position: "relative", overflow: "hidden", ...style }}
    >
      {children}
      {!reduce && (
        <AnimatePresence>
          {ripples.map((r) => (
            <motion.span
              key={r.id}
              initial={{ opacity: 0.4, scale: 0 }}
              animate={{ opacity: 0, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.65, ease: "easeOut" }}
              style={{
                position: "absolute",
                left: r.x,
                top: r.y,
                width: r.size,
                height: r.size,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.5)",
                pointerEvents: "none",
              }}
            />
          ))}
        </AnimatePresence>
      )}
    </motion.button>
  );
}
